/**
 * @fileoverview Poll Voting Hook
 * 
 * Manages poll voting with database persistence.
 * Supports per-image voting for carousel polls.
 * Uses the atomic record_poll_vote function to prevent race conditions.
 * 
 * @module hooks/usePollVotes
 */

import { useState, useCallback, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { logEventDirect } from './useEventLogger';

interface UsePollVotesOptions {
  pollId: string;
}

export function usePollVotes({ pollId }: UsePollVotesOptions) {
  const { user } = useAuth();
  // Track multiple votes (one per image) - format: "imageId:up" or "imageId:down"
  const [userVotes, setUserVotes] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isVoting, setIsVoting] = useState(false);

  // Check if user has already voted on this poll (fetch all their votes)
  useEffect(() => {
    async function checkExistingVotes() {
      if (!user) {
        setIsLoading(false);
        setUserVotes([]);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('poll_votes')
          .select('voted_option')
          .eq('poll_id', pollId)
          .eq('user_id', user.id);

        if (error) {
          console.error('Error checking votes:', error);
        } else if (data && data.length > 0) {
          setUserVotes(data.map(v => v.voted_option));
        }
      } catch (err) {
        console.error('Error checking votes:', err);
      } finally {
        setIsLoading(false);
      }
    }

    checkExistingVotes();
  }, [pollId, user]);

  // Submit a vote - allows multiple votes per poll (one per image)
  // If the user taps the same vote again, it removes the vote
  const submitVote = useCallback(async (votedOption: string) => {
    if (!user || isVoting) return false;
    
    // Extract imageId from votedOption using lastIndexOf to handle IDs with colons
    const lastColonIndex = votedOption.lastIndexOf(':');
    if (lastColonIndex === -1) return false; // Invalid format
    
    const imageId = votedOption.substring(0, lastColonIndex);
    const newVoteType = votedOption.substring(lastColonIndex + 1);
    
    // Check if already voted on this specific image
    const imageFormatVotes = userVotes.filter(v => v.includes(':') && v !== 'yes' && v !== 'no');
    const existingVote = imageFormatVotes.find(v => {
      const voteLastColon = v.lastIndexOf(':');
      if (voteLastColon === -1) return false;
      return v.substring(0, voteLastColon) === imageId;
    });
    
    setIsVoting(true);
    try {
      if (existingVote) {
        const existingVoteType = existingVote.substring(existingVote.lastIndexOf(':') + 1);
        
        if (existingVoteType === newVoteType) {
          // Same vote tapped again → remove the vote
          const { error } = await supabase.rpc('remove_poll_vote', {
            p_poll_id: pollId,
            p_user_id: user.id,
            p_voted_option: existingVote,
          });

          if (error) {
            console.error('Error removing vote:', error);
            return false;
          }

          setUserVotes(prev => prev.filter(v => v !== existingVote));

          logEventDirect('poll_vote_removed', 'engagement', {
            poll_id: pollId,
            vote_option: existingVote,
          }, user.id);

          return true;
        }
        
        // Different vote type on same image → change vote
        const { error } = await supabase.rpc('change_poll_vote', {
          p_poll_id: pollId,
          p_user_id: user.id,
          p_image_id: imageId,
          p_old_vote_type: existingVoteType,
          p_new_vote_type: newVoteType,
        });

        if (error) {
          console.error('Error changing vote:', error);
          return false;
        }

        setUserVotes(prev => prev.filter(v => v !== existingVote).concat(votedOption));
      } else {
        // New vote
        const { error } = await supabase.rpc('record_poll_vote', {
          p_poll_id: pollId,
          p_user_id: user.id,
          p_voted_option: votedOption,
        });

        if (error) {
          if (error.code === '23505') {
            setUserVotes(prev => [...prev, votedOption]);
            return false;
          }
          console.error('Error submitting vote:', error);
          return false;
        }

        setUserVotes(prev => [...prev, votedOption]);
      }

      // Log event for analytics
      logEventDirect('poll_voted', 'engagement', {
        poll_id: pollId,
        vote_option: votedOption,
        is_change: !!existingVote,
      }, user.id);

      // Update persona signals (fire and forget)
      supabase.functions.invoke('update-persona-signal', {
        body: {
          type: 'poll_vote',
          data: { pattern: votedOption },
        },
      }).catch(console.error);

      return true;
    } catch (err) {
      console.error('Error submitting vote:', err);
      return false;
    } finally {
      setIsVoting(false);
    }
  }, [pollId, user, userVotes, isVoting]);

  // For backward compatibility - returns first vote or null
  const userVote = userVotes.length > 0 ? userVotes[0] : null;

  // Separate legacy votes (yes/no) from new imageId:voteType format
  const legacyVotes = userVotes.filter(v => v === 'yes' || v === 'no');
  const imageVotes = userVotes.filter(v => v.includes(':'));

  return {
    userVote, // First vote (backward compat)
    userVotes, // All votes for this poll
    isLoading,
    isVoting,
    submitVote,
    hasVoted: userVotes.length > 0,
    // Helper to check if voted on specific image (only checks new format votes)
    hasVotedOnImage: (imageId: string) => imageVotes.some(v => v.startsWith(`${imageId}:`)),
    getVoteForImage: (imageId: string) => {
      const vote = imageVotes.find(v => v.startsWith(`${imageId}:`));
      if (!vote) return null;
      // Split from the last colon to handle imageIds that may contain colons
      const lastColonIndex = vote.lastIndexOf(':');
      if (lastColonIndex === -1) return null;
      return vote.substring(lastColonIndex + 1) as 'up' | 'down';
    },
    // Legacy votes for backward compatibility
    legacyVotes,
  };
}
